import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import { FC, useCallback } from "react";
import { FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { RateComponents } from "@sinch/components/application/RateComponent/queries.graphql";
import { RateComponentsQuery } from "@sinch/components/application/RateComponent/queries.types";
import { useRateComponentOptions } from "@sinch/components/application/RateComponent/useRateComponentOptions";
import { useBreakPenalty } from "@sinch/components/application/RateInterval/CreateInterval/BreakPenalty/BreakPenaltyProvider";
import { AutocompleteInput } from "@sinch/components/form/AutocompleteInput/AutocompleteInput";
import { useMuiController } from "@sinch/components/index";

/**
 * Rate components whose rates are taken into account when penalizing a missed break
 */
export const PenaltyComponentsField: FC = () => {
  const { t } = useTranslation();
  const { path, disabled } = useBreakPenalty();
  const { field } = useMuiController<FieldValues, string>({ name: `${path}.componentIds`, disabled });

  const { data, loading } = useQueryHandler<RateComponentsQuery>(RateComponents);
  const { options, renderOption } = useRateComponentOptions(data?.rateComponents);
  const selectedIds: string[] = field.value ?? [];
  const allSelected = options.length > 0 && selectedIds.length === options.length;

  const handleSelectAll = useCallback(
    () => field.onChange(options.map(({ value }) => String(value))),
    [field, options]
  );

  return (
    <Stack alignItems="flex-start" spacing={1}>
      <AutocompleteInput
        {...field}
        filterSelectedOptions
        fullWidth
        helperText={t("RateUnit.breakPenaltyComponentsHint")}
        includeInputInList
        label={t("RateUnit.breakPenaltyComponents")}
        loading={loading}
        multiple
        options={options}
        placeholder={t("RateBuilder.selectComponent")}
        renderOption={renderOption}
        value={selectedIds}
      />
      <Button disabled={disabled || allSelected} onClick={handleSelectAll} size="small" variant="text">
        {t("RateUnit.breakPenaltyComponentsSelectAll")}
      </Button>
    </Stack>
  );
};
